import React, { useState, ChangeEvent, useEffect, useRef } from 'react';
import { Grid, Button, TextField, Tooltip } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { ModalWrapper } from 'components/Modals';
import TokenIcon from 'assets/images/token.svg';
import { LoaderButton } from 'components/LoaderButton';
import { RootStore } from 'redux/store';
import { sendShoutouts } from 'redux/thunks';
import { UserIdName } from 'redux/models/userIdName';
import { GraphAutocomplete } from 'components/GraphAutocomplete';
import { shiftCatEyes } from 'helpers/helper';
import './SendShoutOutModal.scss';
import { Alert } from '@material-ui/lab';
import { ShoutOut } from 'redux/models/shoutOut';
import { resetSendingShoutoutSuccess } from 'redux/actions';
import { userHasAcceptedTerms } from 'api/requests';
import { useEasterEggFound } from 'helpers/easterEgghelper';
import EASTER_EGG_IDS from 'helpers/easterEggConstants';

interface SendShoutOutProps {
	open: boolean;
	hideBackdrop: boolean;
	allowClickOutsideToClose?: boolean;
	onBackClicked: () => void;
	onCloseClicked: () => void;
	onsendShoutOutClicked?: () => void;
}

interface UserAndTerms {
	name: string;
	id: string;
	termsAccepted: boolean | null;
}

const SendShoutOutModal: React.FC<SendShoutOutProps> = (props: SendShoutOutProps) => {
	const dispatch = useDispatch();
	const userState = useSelector((state: RootStore) => state.user);
	const teamState = useSelector((state: RootStore) => state.team);
	const [message, setMessage] = useState('');
	const [checkedUsers, setCheckedUsers] = useState<UserAndTerms[]>([]);
	const [recipients, setRecipients] = React.useState<UserIdName[]>([]);
	const unacceptedUsers = useRef([] as string[]);
	const { open, allowClickOutsideToClose, onCloseClicked } = props;
	const [showSenorKitty, setShowSenorKitty] = useState(false);
	const [recipientsError, setRecipientsError] = useState(false);
	const easterEggFound = useEasterEggFound(EASTER_EGG_IDS.LASER_CAT);

	const setMessageValue = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		setMessage(event.target.value);
	};

	const shoutOutValid = () => {
		let error = false;

		if (recipients?.length < 1) {
			setRecipientsError(true);
			error = true;
		}

		if (error) {
			return false;
		}

		return true;
	};

	const shoutItOut = () => {
		if (!shoutOutValid()) {
			return;
		}

		const newShoutOut: ShoutOut = {
			comment: message,
			users: recipients,
		};

		dispatch(sendShoutouts(newShoutOut, userState.selectedTeam?.teamId ? userState.selectedTeam?.teamId : null));
	};

	const selectUser = async (users: any) => {
		let newCheckedUsersList: UserAndTerms[] = [];
		// see which users have accepted terms
		if (users && users.length > 0) {
			const checkedUsersCopy = checkedUsers;

			// get terms for each user and add them to array
			for (let i = 0; i < users.length; i++) {
				let hasBeenChecked = checkedUsers.some(u => u.id === users[i].id);
				if (!hasBeenChecked) {
					let termsAccepted: boolean = await userHasAcceptedTerms(users[i].id);
					checkedUsersCopy.push({ name: users[i].name, id: users[i].id, termsAccepted: termsAccepted });
				}
			}

			// remove users from the terms list if they have been removed from the user list
			newCheckedUsersList = checkedUsersCopy.filter(u => {
				return users.some((user: any) => user.id === u.id);
			});

			setCheckedUsers(newCheckedUsersList);
		}

		setShoutOutRecipients(newCheckedUsersList);
	};

	const setShoutOutRecipients = (checkedUsersList: UserAndTerms[]) => {
		let acceptedUsers: UserIdName[] = [];
		unacceptedUsers.current = [];

		checkedUsersList.forEach((recipient: UserAndTerms) => {
			if (!recipient.termsAccepted) {
				const updatedUnacceptedUsers: string[] = [...unacceptedUsers.current];
				updatedUnacceptedUsers.push(recipient.name);
				unacceptedUsers.current = updatedUnacceptedUsers;
			} else {
				acceptedUsers.push(recipient);
			}
		});

		const updatedUsers: UserIdName[] = acceptedUsers.map((u: any) => {
			return { id: u.id, name: u.name };
		});
		setRecipients(updatedUsers);
		if (recipientsError) {
			setRecipientsError(false);
		}
	};

	const loaderButtonProps = {
		loading: teamState.sendingShoutOut,
		onHandleClick: shoutItOut,
	};

	const toggleCat = () => {
		setShowSenorKitty(!showSenorKitty);
		if (!showSenorKitty) {
			easterEggFound();
		}
	};

	useEffect(() => {
		if (teamState.sendingShoutOutSuccess) {
			dispatch(resetSendingShoutoutSuccess());
			onCloseClicked();
		}
	}, [teamState.sendingShoutOutSuccess, dispatch, onCloseClicked]);

	useEffect(() => {
		if (showSenorKitty) {
			document.body.style.cursor = 'none';
			document.addEventListener('mousemove', shiftCatEyes, false);
			document.addEventListener('touchmove', shiftCatEyes, false);
		}

		return () => {
			if (showSenorKitty) {
				document.body.style.cursor = 'unset';
				document.removeEventListener('mousemove', shiftCatEyes, false);
				document.removeEventListener('touchmove', shiftCatEyes, false);
			} else {
				return;
			}
		};
	}, [showSenorKitty]);

	useEffect(() => {
		if (open) {
			setMessage('');
			unacceptedUsers.current = [];
		}
	}, [open]);

	return (
		<div id='shout-out-wrapper'>
			<ModalWrapper open={open} overflowInitial={true} onCloseClicked={onCloseClicked} allowClickOutsideToClose={allowClickOutsideToClose}>
				<Grid container direction='column' alignItems='center' className='send-shout-out glow'>
					<div className='content-wrapper'>
						<Tooltip title='🥚'>
							<img
								src={TokenIcon}
								className={`send-shout-out__icon ${!showSenorKitty ? 'token-icon' : 'token-icon transparent'}`}
								alt='toa-token'
								id='send-tokens-icon'
								onClick={() => toggleCat()}
							/>
						</Tooltip>
						<div id='catBox' className={`cat-box ${showSenorKitty ? 'token-icon' : 'token-icon transparent'}`} onClick={() => toggleCat()}>
							<div className='cat-face'>
								<div className='pupil'></div>
								<div id='cat-eyes' className='cat-eyes'>
									<div className='left-eye'></div>
									<div className='right-eye'></div>
								</div>
								<div className='cat-nose'></div>
								<div className='cat-mouth'></div>
							</div>
						</div>
						<div className='send-shout-out__title'>Send a Shout-Out</div>

						<Grid container item className='send-shout-out__innergrid'>
							<Grid container item xs={12} alignItems='center'>
								<p>
									Shout-outs are a great way to recognize people across multiple teams! Your shout-out will be posted on the board for each
									team the recipients are apart of. The recipients' team members will then see the shout-out and can optionally provide the
									recipients with tokens in recognition of their spotlighted contributions.
								</p>
							</Grid>

							<Grid container item xs={12} alignItems='center'>
								<GraphAutocomplete
									currentUserId={userState.userId}
									onSelectUser={selectUser}
									tagLimit={4}
									searchUsersOnly={true}
									fieldError={recipientsError}
								></GraphAutocomplete>
							</Grid>

							<Grid item xs={12} className='send-shout-out__innergrid-message-item'>
								<TextField
									rows={2}
									multiline
									value={message}
									fullWidth={true}
									label='Message'
									variant='outlined'
									id='toknes-text'
									onChange={event => setMessageValue(event)}
								/>
							</Grid>
						</Grid>
						{unacceptedUsers.current.length > 0 && (
							<Alert severity='warning' className='send-tokens__alert'>
								{unacceptedUsers.current.join(', ')} {unacceptedUsers.current.length > 1 ? 'are' : 'is'} unable to receive shout-outs because{' '}
								{unacceptedUsers.current.length > 1 ? 'they have' : 'this user has'} not accepted the Terms of Use.
							</Alert>
						)}
						<div className='send-shout-out__button-container'>
							<Button variant='contained' color='primary' className='send-shout-out__button-container--cancel' onClick={onCloseClicked}>
								cancel
							</Button>
							<LoaderButton type='button' {...loaderButtonProps}>
								Send
							</LoaderButton>
						</div>
					</div>
				</Grid>
			</ModalWrapper>
		</div>
	);
};

export default SendShoutOutModal;
